// PresentationViewer.jsx
import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography, Paper, CircularProgress } from '@mui/material';
import {
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Fullscreen as FullscreenIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import apiClient from './utils/apiClient';

export default function PresentationViewer({
  userId,
  reportId,
  onClose,
  jsonFilename = "summary_data.json",
  dataLabel = "summary",
}) {
  const [presentationData, setPresentationData] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const contentRef = React.useRef(null);

  useEffect(() => {
    const loadPresentation = async () => {
      try {
        setLoading(true);
        const data = await apiClient.getRaw(
          `/report-path/${userId}/work/${reportId}/final_outputs/${jsonFilename}`
        );
        setPresentationData(data);
        setError(null);
      } catch (err) {
        console.error(`Error loading ${dataLabel}:`, err);
        setError(`Failed to load ${dataLabel} data`);
      } finally {
        setLoading(false);
      }
    };

    loadPresentation();
  }, [userId, reportId, jsonFilename, dataLabel]);

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => Math.max(0, prev - 1));
  };

  const handleNextSlide = () => {
    if (presentationData) {
      setCurrentSlide((prev) =>
        Math.min(presentationData.slides.length - 1, prev + 1)
      );
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'ArrowLeft') handlePrevSlide();
    if (e.key === 'ArrowRight') handleNextSlide();
    if (e.key === 'Escape') {
      if (isFullscreen) setIsFullscreen(false);
      else onClose();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isFullscreen, presentationData]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Reset scroll position when entering fullscreen
  useEffect(() => {
    if (isFullscreen && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [isFullscreen]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!presentationData || !presentationData.slides.length) {
    return (
      <Box p={3}>
        <Typography>No {dataLabel} data available</Typography>
      </Box>
    );
  }

  const slide = presentationData.slides[currentSlide];

  return (
    <Box
      sx={{
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 0 : 'auto',
        left: isFullscreen ? 0 : 'auto',
        width: isFullscreen ? '100vw' : '100%',
        height: isFullscreen ? '100vh' : 'auto',
        zIndex: isFullscreen ? 9999 : 'auto',
        backgroundColor: isFullscreen ? '#000' : 'transparent',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          backgroundColor: isFullscreen ? 'rgba(0,0,0,0.8)' : '#f5f5f5',
        }}
      >
        <Typography variant="h6" sx={{ color: isFullscreen ? '#fff' : 'inherit' }}>
          {presentationData.title}
        </Typography>
        <Box>
          <IconButton 
            onClick={handlePrevSlide} 
            disabled={currentSlide === 0}
            sx={{ color: isFullscreen ? '#fff' : 'inherit' }}
          >
            <PrevIcon />
          </IconButton>
          <Typography
            component="span"
            sx={{ mx: 2, color: isFullscreen ? '#fff' : 'inherit' }}
          >
            {currentSlide + 1} / {presentationData.slides.length}
          </Typography>
          <IconButton
            onClick={handleNextSlide}
            disabled={currentSlide === presentationData.slides.length - 1}
            sx={{ color: isFullscreen ? '#fff' : 'inherit' }}
          >
            <NextIcon />
          </IconButton>
          <IconButton onClick={toggleFullscreen} sx={{ color: isFullscreen ? '#fff' : 'inherit' }}>
            <FullscreenIcon />
          </IconButton>
          {isFullscreen && (
            <IconButton onClick={() => setIsFullscreen(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Slide Content */}
      <Paper
        ref={contentRef}
        elevation={3}
        sx={{
          flex: 1,
          m: isFullscreen ? 0 : 2,
          p: 4,
          backgroundColor: '#fff',
          overflow: 'auto',
          height: isFullscreen ? 'calc(100vh - 80px)' : 'auto',
          minHeight: isFullscreen ? 'unset' : '500px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: isFullscreen ? 'flex-start' : 'center',
        }}
      >
        {/* Text Content */}
        {slide.text_content.map((item, idx) => (
          <Typography
            key={idx}
            variant={item.type === 'title' ? 'h4' : 'body1'}
            sx={{
              mb: 2,
              fontWeight: item.type === 'title' ? 'bold' : 'normal',
              textAlign: item.type === 'title' ? 'center' : 'left',
            }}
          >
            {item.text}
          </Typography>
        ))}

        {/* Images */}
        {slide.images.map((image, idx) => (
          <Box
            key={idx}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              my: 2,
            }}
          >
            <img
              src={image.data}
              alt={`Slide ${currentSlide + 1} Image ${idx + 1}`}
              style={{
                maxWidth: '100%',
                maxHeight: isFullscreen ? '70vh' : '400px',
                objectFit: 'contain',
              }}
            />
          </Box>
        ))}

        {/* Notes */}
        {slide.notes && (
          <Box sx={{ mt: 4, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Notes:
            </Typography>
            <Typography variant="body2">{slide.notes}</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
